import { useState, useEffect } from 'react';
import { ShareNetwork, X, Clock, Lock, Users, Buildings, UserCircle } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { api } from '../../lib/api-client';
import type { SceneShare, SceneInvite } from '@spine-workspace/shared';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  sceneId: string;
}

interface SharesData {
  shares: SceneShare[];
  invites: SceneInvite[];
  orgShared: boolean;
}

type ShareMode = 'private' | 'users' | 'org';

export default function ShareDialog({ open, onClose, sceneId }: ShareDialogProps) {
  const [mode, setMode] = useState<ShareMode>('private');

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState<'view' | 'edit'>('view');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const [sharesData, setSharesData] = useState<SharesData>({ shares: [], invites: [], orgShared: false });
  const [sharesLoading, setSharesLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  useEffect(() => {
    if (open) loadShares();
  }, [open, sceneId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive mode from sharesData
  useEffect(() => {
    if (sharesData.orgShared) setMode('org');
    else if (sharesData.shares.length > 0 || sharesData.invites.length > 0) setMode('users');
    else setMode('private');
  }, [sharesData]);

  async function loadShares() {
    setSharesLoading(true);
    try {
      const res = await api.get<SharesData>(`/api/scenes/${sceneId}/shares`);
      setSharesData(res);
    } catch {
      // may not be owner
    } finally {
      setSharesLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteLoading(true);
    try {
      const res = await api.post<{ ok: boolean; status: 'active' | 'pending' }>(
        `/api/scenes/${sceneId}/invite`,
        { email: inviteEmail, permission: invitePermission }
      );
      setInviteEmail('');
      setInviteSuccess(
        res.status === 'pending'
          ? `Invite sent to ${inviteEmail} — they'll get access when they register.`
          : `Access granted.`
      );
      await loadShares();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  }

  async function changePermission(userId: string, permission: 'view' | 'edit') {
    try {
      await api.patch(`/api/scenes/${sceneId}/shares/${userId}`, { permission });
      setSharesData((prev) => ({
        ...prev,
        shares: prev.shares.map((s) => s.userId === userId ? { ...s, permission } : s),
      }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update permission');
    }
  }

  async function revokeShare(userId: string) {
    try {
      await api.delete(`/api/scenes/${sceneId}/shares/${userId}`);
      setSharesData((prev) => ({ ...prev, shares: prev.shares.filter((s) => s.userId !== userId) }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke access');
    }
  }

  async function cancelInvite(inviteId: string) {
    try {
      await api.delete(`/api/scenes/${sceneId}/invites/${inviteId}`);
      setSharesData((prev) => ({ ...prev, invites: prev.invites.filter((i) => i.id !== inviteId) }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel invite');
    }
  }

  async function toggleOrgShare(enable: boolean) {
    setOrgLoading(true);
    try {
      if (enable) {
        await api.post(`/api/scenes/${sceneId}/share-org`, {});
      } else {
        await api.delete(`/api/scenes/${sceneId}/share-org`);
      }
      setSharesData((prev) => ({ ...prev, orgShared: enable }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update org sharing');
    } finally {
      setOrgLoading(false);
    }
  }

  const selectCls = `bg-bg-input border border-border text-text-secondary rounded px-1.5 py-0.5 text-xs font-sans
    focus:outline-none focus:border-accent transition-all duration-fast`;

  const modes: { key: ShareMode; label: string; desc: string; icon: React.ReactNode }[] = [
    {
      key: 'private',
      label: 'Private',
      desc: 'Only you can access',
      icon: <UserCircle size={15} />,
    },
    {
      key: 'users',
      label: 'Specific people',
      desc: 'Invite by email',
      icon: <Users size={15} />,
    },
    {
      key: 'org',
      label: 'Organization',
      desc: 'All @eightherastudios.com & @thenicegang.com',
      icon: <Buildings size={15} />,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShareNetwork size={18} className="text-accent" />
            <DialogTitle>Share Scene</DialogTitle>
          </div>
          <DialogDescription>
            Control who can view or edit this scene.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Mode selector ── */}
          <div className="grid grid-cols-3 gap-1.5">
            {modes.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => {
                  if (key === 'org' && !sharesData.orgShared) toggleOrgShare(true);
                  if (key !== 'org' && sharesData.orgShared) toggleOrgShare(false);
                  setMode(key);
                }}
                disabled={orgLoading}
                className={`flex flex-col items-center gap-1 py-3 px-2 rounded-lg border text-center transition-all duration-fast
                  ${mode === key
                    ? 'border-accent bg-accent-muted text-accent'
                    : 'border-border text-text-secondary hover:border-border-strong hover:text-text-primary hover:bg-bg-hover'
                  } disabled:opacity-50`}
              >
                {icon}
                <span className="font-sans text-[11px] font-medium">{label}</span>
              </button>
            ))}
          </div>

          {/* Mode description */}
          <p className="font-sans text-xs text-text-tertiary">
            {modes.find((m) => m.key === mode)?.desc}
          </p>

          {/* ── Org sharing active notice ── */}
          {mode === 'org' && (
            <div className="bg-accent-muted border border-accent/20 rounded-lg px-3 py-2.5 flex items-start gap-2">
              <Buildings size={14} className="text-accent mt-0.5 shrink-0" />
              <p className="font-sans text-xs text-text-primary">
                All members of <strong>@eightherastudios.com</strong> and <strong>@thenicegang.com</strong> can view this scene after signing in.
              </p>
            </div>
          )}

          {/* ── Invite by email (shown in 'users' mode) ── */}
          {mode === 'users' && (
            <div>
              <p className="font-sans text-xs font-semibold uppercase tracking-widest text-text-tertiary mb-2">
                Invite by email
              </p>
              <form onSubmit={handleInvite} className="flex gap-2">
                <input
                  type="email"
                  placeholder="colleague@eightherastudios.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="flex-1 bg-bg-input border border-border text-text-primary rounded-md px-3 py-[7px] text-sm font-sans placeholder:text-text-tertiary hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
                />
                <select
                  value={invitePermission}
                  onChange={(e) => setInvitePermission(e.target.value as 'view' | 'edit')}
                  className="bg-bg-input border border-border text-text-secondary rounded-md px-2 py-[7px] text-xs font-sans focus:outline-none focus:border-accent transition-all duration-normal"
                >
                  <option value="view">Viewer</option>
                  <option value="edit">Editor</option>
                </select>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="bg-accent text-white font-sans font-medium text-xs px-3 py-[7px] rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal disabled:opacity-50"
                >
                  {inviteLoading ? '…' : 'Invite'}
                </button>
              </form>
              {inviteError && <p className="font-sans text-xs text-error mt-1.5">{inviteError}</p>}
              {inviteSuccess && <p className="font-sans text-xs text-success mt-1.5">{inviteSuccess}</p>}
            </div>
          )}

          {/* ── People with access (shown when users mode has entries) ── */}
          {!sharesLoading && mode === 'users' && (sharesData.shares.length > 0 || sharesData.invites.length > 0) && (
            <div>
              <p className="font-sans text-xs font-semibold uppercase tracking-widest text-text-tertiary mb-2">
                People with access
              </p>
              <div className="space-y-1">
                {sharesData.shares.map((s) => (
                  <div key={s.userId} className="flex items-center gap-2 py-1">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-accent to-violet flex items-center justify-center text-white text-[10px] font-sans font-medium shrink-0">
                      {(s.userName ?? s.userEmail)[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-xs text-text-primary truncate">{s.userName}</p>
                      <p className="font-sans text-[10px] text-text-tertiary truncate">{s.userEmail}</p>
                    </div>
                    <select
                      value={s.permission}
                      onChange={(e) => changePermission(s.userId, e.target.value as 'view' | 'edit')}
                      className={selectCls}
                    >
                      <option value="view">Viewer</option>
                      <option value="edit">Editor</option>
                    </select>
                    <button
                      onClick={() => revokeShare(s.userId)}
                      className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast shrink-0"
                      title="Revoke access"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}

                {sharesData.invites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-2 py-1">
                    <div className="w-7 h-7 rounded-full bg-bg-elevated border border-border flex items-center justify-center text-text-tertiary shrink-0">
                      <Clock size={12} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-xs text-text-primary truncate">{inv.invitedEmail}</p>
                      <p className="font-sans text-[10px] text-text-tertiary">Pending invite</p>
                    </div>
                    <span className="font-sans text-[10px] text-text-tertiary capitalize">{inv.permission}</span>
                    <button
                      onClick={() => cancelInvite(inv.id)}
                      className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast shrink-0"
                      title="Cancel invite"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Public link — locked ── */}
          <div className="border-t border-border-subtle pt-3">
            <div className="flex items-center gap-2 text-text-tertiary">
              <Lock size={13} />
              <span className="font-sans text-xs font-semibold uppercase tracking-widest">Public link</span>
              <span className="ml-auto font-sans text-[10px] bg-bg-elevated border border-border px-2 py-0.5 rounded text-text-tertiary">
                Disabled
              </span>
            </div>
            <p className="font-sans text-[11px] text-text-tertiary mt-1">
              Public sharing is disabled for this workspace. Only signed-in members can access scenes.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
